import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { RootState } from '../../store/store';
import type { Guild, GuildRole, GuildEmoji, WatchedGuild } from '../../types';

// Helper to safely access auth token from state
const getAuthToken = (state: unknown): string | undefined => {
  const rootState = state as RootState;
  return (rootState as { auth: { auth: { access_token?: string } | null } }).auth?.auth?.access_token;
};

export const guildsApi = createApi({
  reducerPath: 'guildsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api',
    prepareHeaders: (headers, { getState }) => {
      const token = getAuthToken(getState());
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['GuildRoles', 'WatchedGuilds'],
  endpoints: (builder) => ({
    // Get guilds where the bot is present
    getBotGuilds: builder.query<Guild[], void>({
      query: () => 'guilds/bot-guilds',
    }),
    getUserGuilds: builder.query<Guild[], void>({
      async queryFn(_arg, _queryApi, _extraOptions, _fetchWithBQ) {
        const token = getAuthToken(_queryApi.getState());

        if (!token) {
          return {
            error: {
              status: 401,
              data: 'Not authenticated'
            } as FetchBaseQueryError
          };
        }

        const result: Response = await fetch('https://discord.com/api/v10/users/@me/guilds', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!result.ok) {
          return {
            error: {
              status: result.status,
              data: 'Failed to fetch guilds'
            } as FetchBaseQueryError
          };
        }

        const data: Guild[] = await result.json();
        return { data };
      },
    }),
    checkGuildStatus: builder.query<Record<string, boolean>, string>({
      query: (guildIds) => `guilds/bot-status?ids=${guildIds}`,
    }),
    getGuildRoles: builder.query<GuildRole[], string>({
      query: (guildId) => `guild/${guildId}/roles`,
      providesTags: ['GuildRoles'],
    }),
    getGuildEmojis: builder.query<GuildEmoji[], string>({
      query: (guildId) => `guild/${guildId}/emojis`,
    }),
    checkAdminStatus: builder.query<Record<string, boolean>, string>({
      query: (guildIds) => `guilds/admin-status?ids=${guildIds}`,
    }),
    getWatchedGuilds: builder.query<WatchedGuild[], void>({
      query: () => 'users/me/watched-guilds',
      providesTags: ['WatchedGuilds'],
    }),
    addWatchedGuild: builder.mutation<WatchedGuild, { guildId: string }>({
      query: ({ guildId }) => ({
        url: 'users/me/watched-guilds',
        method: 'POST',
        body: { guildId },
      }),
      invalidatesTags: ['WatchedGuilds'],
    }),
    removeWatchedGuild: builder.mutation<{ success: boolean }, { guildId: string }>({
      query: ({ guildId }) => ({
        url: `users/me/watched-guilds/${guildId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['WatchedGuilds'],
    }),
  }),
});

// Export auto-generated hooks
export const {
  useGetBotGuildsQuery,
  useGetUserGuildsQuery,
  useCheckGuildStatusQuery,
  useGetGuildRolesQuery,
  useGetGuildEmojisQuery,
  useCheckAdminStatusQuery,
  useGetWatchedGuildsQuery,
  useAddWatchedGuildMutation,
  useRemoveWatchedGuildMutation,
} = guildsApi;
